import {
    MutableRefObject,
    ReactNode,
    ReactElement,
    useCallback,
    useState,
    useRef,
    useEffect
} from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { FormattedMessage, useIntl } from "react-intl";
import Slider from "react-slick";

import { media } from "@atlaskit/primitives";
import FeatureGates from "@atlassian/feature-gate-js-client";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { Adg4Button } from "@/components/adg4/button/src";
import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { Adg4TextUnderline } from "@/components/adg4/text-underline/src";
import { Picture } from "@/components/wpl/image/src";
import { Video } from "@/components/wpl/video/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    DataEventProps,
    Row,
    TypographyStyles
} from "@/libraries/wpl/foundations/src";

/* variant 1 data */

type JiraView = {
    videoLink: string;
    image: string;
    videoLength: number;
};

const jiraViews: JiraView[] = [
    {
        videoLink:
            "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_A_LowBR.mp4",
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_A_HD (0-00-00-00)-4060786602.webp",
        videoLength: 9400
    },
    {
        videoLink:
            "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_B_LowBR.mp4",
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_B_HD (0-00-09-24)-1969559334.webp",
        videoLength: 7180
    },
    {
        videoLink:
            "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_C_LowBR.mp4",
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_C_HD (0-00-16-35)-1443600009.webp",
        videoLength: 4750
    }
];

/* variant 2 data */

type TeamType =
    | "default"
    | "software"
    | "product-management"
    | "marketing"
    | "project-management"
    | "design"
    | "it";

type Team = {
    videoLink?: string;
    image: string;
};

const SoftwareIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
        >
            <path
                d="M37.97 12.4648H10.03V30.7598H37.97V12.4648Z"
                fill="#BF63F3"
            />
            <path
                d="M31.505 33.2651V36.3751H16.495V33.2651H21.495V27.6851H26.505V33.2651H31.505Z"
                fill="#FCA700"
            />
            <path
                d="M26.505 27.6851H21.495V30.7651H26.505V27.6851Z"
                fill="#AE2A19"
            />
            <path
                d="M24 25.9549C26.397 25.9549 28.34 24.0118 28.34 21.6149C28.34 19.218 26.397 17.2749 24 17.2749C21.6031 17.2749 19.66 19.218 19.66 21.6149C19.66 24.0118 21.6031 25.9549 24 25.9549Z"
                fill="white"
            />
        </svg>
    );
};

const ProductManagementIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.41 11.2451H21.075L22.54 15.1601L26.665 14.4751L28.995 18.5151L26.34 21.7451L28.995 24.9701L26.665 29.0101L22.545 28.3251L21.08 32.2401H16.415L14.95 28.3251L10.83 29.0101L8.5 24.9701L11.155 21.7451L8.5 18.5201L10.83 14.4801L14.955 15.1651L16.42 11.2501L16.41 11.2451ZM18.74 25.6751C20.915 25.6751 22.675 23.9151 22.675 21.7401C22.675 19.5651 20.915 17.8051 18.74 17.8051C16.565 17.8051 14.805 19.5651 14.805 21.7401C14.805 23.9151 16.565 25.6751 18.74 25.6751Z"
                fill="#82B536"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M29.555 20.52H33.405L34.615 23.75L38.02 23.185L39.945 26.52L37.75 29.185L39.94 31.85L38.015 35.185L34.61 34.62L33.4 37.85H29.55L28.34 34.62L24.935 35.185L23.01 31.85L25.2 29.185L23.01 26.52L24.935 23.185L28.34 23.75L29.55 20.52H29.555ZM31.48 32.435C33.275 32.435 34.73 30.98 34.73 29.185C34.73 27.39 33.275 25.935 31.48 25.935C29.685 25.935 28.23 27.39 28.23 29.185C28.23 30.98 29.685 32.435 31.48 32.435Z"
                fill="#FCA700"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M26.34 21.74L28.995 18.515L26.665 14.475L22.54 15.16L21.075 11.25H16.41L14.94 15.16L10.82 14.475L8.48999 18.515L11.145 21.74L8.48999 24.97L10.82 29.01L14.94 28.32L16.41 32.235H21.075L22.54 28.32L24.81 28.7L26.665 29.01L28.995 24.97L27.935 23.68L26.34 21.74ZM18.74 25.675C16.57 25.675 14.805 23.915 14.805 21.74C14.805 19.565 16.57 17.805 18.74 17.805C20.91 17.805 22.675 19.57 22.675 21.74C22.675 23.91 20.915 25.675 18.74 25.675Z"
                fill="#82B536"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M27.935 23.6801L24.94 23.1851L23.015 26.5201L24.81 28.7001L26.665 29.0101L28.995 24.9701L27.935 23.6801Z"
                fill="#AE2A19"
            />
        </svg>
    );
};

const MarketingIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
        >
            <path
                d="M35.275 13.0898L24.365 17.4548V29.0948L35.275 33.4598V13.0898Z"
                fill="#1868DA"
            />
            <path
                d="M27.16 18.1797V28.3497H21.455V34.9097H17.09V28.3497H12.725V18.1797H27.16Z"
                fill="#FCA700"
            />
            <path
                d="M27.16 18.1797H24.365V28.3447H27.16V18.1797Z"
                fill="#AE2A19"
            />
        </svg>
    );
};

const ProjectManagementIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
        >
            <path
                d="M39.93 21.7451H15.575V33.6451H39.93V21.7451Z"
                fill="#BF63F3"
            />
            <path d="M32.36 14.355H8.005V26.255H32.36V14.355Z" fill="#FCA700" />
            <path
                d="M32.36 21.7451H15.575V26.2551H32.36V21.7451Z"
                fill="#AE2A19"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M38.415 26.045L33.17 31.29L30.46 28.505L31.63 27.37L33.19 28.97L37.265 24.895L38.42 26.05L38.415 26.045Z"
                fill="white"
            />
        </svg>
    );
};

const DesignIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
        >
            <path
                d="M30.515 25.3153C30.595 24.8703 30.635 24.3903 30.635 23.8903C30.635 23.3903 30.595 22.9003 30.51 22.4453C30.025 19.7503 28.155 18.0903 25.525 17.6603C25.075 17.5853 24.605 17.5503 24.12 17.5503C23.635 17.5503 23.12 17.5903 22.655 17.6703C20.03 18.1103 18.16 19.7653 17.675 22.4453C17.595 22.9003 17.55 23.3803 17.55 23.8903C17.55 24.4003 17.59 24.8703 17.67 25.3153C18.15 27.9903 20.02 29.6503 22.655 30.0903C23.12 30.1703 23.61 30.2103 24.12 30.2103C24.63 30.2103 25.08 30.1753 25.525 30.1003C28.155 29.6703 30.03 28.0103 30.51 25.3153H30.515Z"
                fill="#FCA700"
            />
            <path
                d="M8.845 23.1801L16.645 9.81006L24.445 23.1801H8.845Z"
                fill="#BF63F3"
            />
            <path
                d="M21.415 17.9902C19.445 18.6802 18.075 20.2052 17.675 22.4452C17.63 22.6852 17.6 22.9252 17.575 23.1802H24.44L21.415 17.9902Z"
                fill="#AE2A19"
            />
            <path
                d="M35.9 25.0601H24.94V36.0201H35.9V25.0601Z"
                fill="#82B536"
            />
            <path
                d="M24.94 25.0649V30.1749C25.14 30.1549 25.335 30.1299 25.53 30.0999C28.16 29.6749 30.03 28.0099 30.515 25.3149C30.53 25.2349 30.545 25.1499 30.555 25.0649H24.94Z"
                fill="#AE2A19"
            />
        </svg>
    );
};

const ITIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.025 17V37.7L18.02 32.56H36.015V17H12.025Z"
                fill="#FCA700"
            />
            <path
                d="M22.68 17.615C22.68 16.13 23.865 14.94 25.33 14.94C26.795 14.94 27.98 16.125 27.98 17.615C27.98 18.37 27.8 18.715 27.635 18.935C27.44 19.19 27.195 19.37 26.765 19.685C26.695 19.74 26.615 19.795 26.53 19.855C25.995 20.25 25.315 20.79 24.805 21.685C24.29 22.585 24.005 23.715 24.005 25.21H26.655C26.655 24.05 26.87 23.405 27.1 23.01C27.33 22.605 27.645 22.33 28.105 21.99C28.16 21.95 28.22 21.905 28.285 21.86C28.7 21.56 29.29 21.135 29.735 20.55C30.31 19.8 30.63 18.86 30.63 17.615C30.63 14.685 28.28 12.29 25.33 12.29C22.38 12.29 20.03 14.685 20.03 17.615V18.94H22.68V17.615Z"
                fill="#1868DB"
            />
            <path
                d="M25.33 27.2051C24.415 27.2051 23.675 27.9451 23.675 28.8601C23.675 29.7751 24.415 30.5151 25.33 30.5151C26.245 30.5151 26.985 29.7751 26.985 28.8601C26.985 27.9451 26.245 27.2051 25.33 27.2051Z"
                fill="#1868DB"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20.06 17H22.75C22.7 17.195 22.675 17.405 22.675 17.615V18.94H20.025V17.615C20.025 17.41 20.035 17.205 20.06 17Z"
                fill="#AE2A19"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M26.985 28.8601C26.985 29.7751 26.245 30.5201 25.33 30.5201C24.415 30.5201 23.67 29.7751 23.67 28.8601C23.67 27.9451 24.415 27.2051 25.33 27.2051C26.245 27.2051 26.985 27.9451 26.985 28.8601Z"
                fill="#AE2A19"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M30.63 17.615C30.63 18.865 30.31 19.8 29.735 20.55C29.29 21.135 28.7 21.56 28.285 21.86C28.22 21.91 28.16 21.95 28.105 21.99C27.645 22.335 27.33 22.61 27.1 23.015C26.87 23.415 26.655 24.06 26.655 25.215H24.005C24.005 23.72 24.285 22.59 24.8 21.695C25.315 20.8 25.995 20.26 26.525 19.86C26.61 19.8 26.69 19.745 26.76 19.69C27.195 19.37 27.435 19.195 27.63 18.94C27.8 18.715 27.98 18.37 27.98 17.615C27.98 17.405 27.955 17.195 27.905 17H30.595C30.62 17.205 30.63 17.41 30.63 17.615Z"
                fill="#AE2A19"
            />
        </svg>
    );
};

const SoftwareHoverIcon = () => {
    return (
        <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M37.97 12.4648H10.03V30.7598H37.97V12.4648Z"
                fill="#BF63F3"
            />
            <path
                d="M31.505 33.2651V36.3751H16.495V33.2651H21.495V27.6851H26.505V33.2651H31.505Z"
                fill="#FCA700"
            />
            <path
                d="M26.505 27.6851H21.495V30.7651H26.505V27.6851Z"
                fill="#AE2A19"
            />
            <path
                d="M24 25.9549C26.397 25.9549 28.34 24.0118 28.34 21.6149C28.34 19.218 26.397 17.2749 24 17.2749C21.6031 17.2749 19.66 19.218 19.66 21.6149C19.66 24.0118 21.6031 25.9549 24 25.9549Z"
                fill="white"
            />
            <path
                d="M38.5963 12.6714C39.0104 12.6364 44.5511 11.3216 43.0977 11.0363C42.5586 11.1845 42.1396 11.0347 41.8701 11.4363C41.7921 10.963 40.735 11.7184 40.5726 11.7471C40.4995 11.3885 38.8431 12.3113 39.0851 11.9782C38.3332 11.9877 37.3995 12.2459 36.4284 12.5487C35.9526 12.2316 37.9922 9.9685 37.1348 10.4578C37.1949 10.3303 37.4255 9.77886 37.3946 10.1438C37.6658 9.83145 40.618 5.05034 39.3904 5.85994C38.5622 6.84644 37.9402 8.49912 37.2387 9.3151C36.5567 9.59081 36.0841 11.959 35.3242 11.7232C34.8808 11.1909 33.7376 4.67264 32.9257 5.25115C33.0669 5.75795 32.9387 6.21215 33.3333 6.45598C32.851 6.53248 33.6207 7.56998 33.6499 7.72935C33.2927 7.79629 34.2183 9.42824 33.8854 9.18919C33.8919 9.64977 33.9974 10.1821 34.1501 10.7478C33.9471 10.5964 33.7246 10.2506 33.5963 10.1996C32.229 9.65614 31.193 8.40667 29.7688 7.72935C28.572 9.22584 39.1549 13.0013 32.5928 13.5655C31.3586 14.356 29.7185 14.4771 28.3414 15.2373C28.2229 16.8836 36.0728 12.3415 33.8074 14.9504C33.4551 16.0533 32.9322 17.2916 32.2095 18.3785C31.7483 22.5237 35.67 11.2626 35.5077 15.5592C36.0484 16.5856 36.5713 17.8223 36.8392 19.0956C38.7571 21.8559 36.1394 14.5536 35.8649 13.5193C36.9042 13.0842 40.9217 18.0183 41.6492 17.2406C41.6833 17.1306 41.1052 16.5202 40.9298 16.4549C40.8113 16.5138 40.7512 16.5616 40.6814 16.4533C41.0338 16.1202 39.798 15.6995 39.6811 15.5847C39.9409 15.3313 38.2212 14.5074 38.6385 14.5201C38.317 14.0787 37.8249 13.634 37.2647 13.1862C38.2585 12.9981 38.6905 12.3096 38.5995 12.6682L38.5963 12.6714Z"
                fill="#101214"
            />
        </svg>
    );
};

const ProductManagementHoverIcon = () => {
    return (
        <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.41 11.2451H21.075L22.54 15.1601L26.665 14.4751L28.995 18.5151L26.34 21.7451L28.995 24.9701L26.665 29.0101L22.545 28.3251L21.08 32.2401H16.415L14.95 28.3251L10.83 29.0101L8.5 24.9701L11.155 21.7451L8.5 18.5201L10.83 14.4801L14.955 15.1651L16.42 11.2501L16.41 11.2451ZM18.74 25.6751C20.915 25.6751 22.675 23.9151 22.675 21.7401C22.675 19.5651 20.915 17.8051 18.74 17.8051C16.565 17.8051 14.805 19.5651 14.805 21.7401C14.805 23.9151 16.565 25.6751 18.74 25.6751Z"
                fill="#82B536"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M29.555 20.52H33.405L34.615 23.75L38.02 23.185L39.945 26.52L37.75 29.185L39.94 31.85L38.015 35.185L34.61 34.62L33.4 37.85H29.55L28.34 34.62L24.935 35.185L23.01 31.85L25.2 29.185L23.01 26.52L24.935 23.185L28.34 23.75L29.55 20.52H29.555ZM31.48 32.435C33.275 32.435 34.73 30.98 34.73 29.185C34.73 27.39 33.275 25.935 31.48 25.935C29.685 25.935 28.23 27.39 28.23 29.185C28.23 30.98 29.685 32.435 31.48 32.435Z"
                fill="#FCA700"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M26.34 21.74L28.995 18.515L26.665 14.475L22.54 15.16L21.075 11.25H16.41L14.94 15.16L10.82 14.475L8.48999 18.515L11.145 21.74L8.48999 24.97L10.82 29.01L14.94 28.32L16.41 32.235H21.075L22.54 28.32L24.81 28.7L26.665 29.01L28.995 24.97L27.935 23.68L26.34 21.74ZM18.74 25.675C16.57 25.675 14.805 23.915 14.805 21.74C14.805 19.565 16.57 17.805 18.74 17.805C20.91 17.805 22.675 19.57 22.675 21.74C22.675 23.91 20.915 25.675 18.74 25.675Z"
                fill="#82B536"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M27.935 23.6801L24.94 23.1851L23.015 26.5201L24.81 28.7001L26.665 29.0101L28.995 24.9701L27.935 23.6801Z"
                fill="#AE2A19"
            />
            <path
                d="M43.8772 20.5715C43.8633 20.4818 43.8353 20.3259 43.7594 20.1053L43.8478 20.169C43.4203 19.2989 43.007 18.321 42.3138 17.6925C42.2817 17.6707 42.2267 17.6347 42.2211 17.6626C42.1369 17.7614 41.9628 17.4997 41.859 17.4085C41.7411 16.4496 38.9527 15.2764 38.0729 14.7283C38.0935 14.7293 38.2178 14.7379 38.2345 14.7378C38.0442 14.3956 34.6344 13.7391 35.3836 13.744C35.0831 13.6444 34.7581 13.554 34.4148 13.4758C34.5649 12.5347 34.3631 11.4826 34.0371 10.6794L34.0326 10.7256C32.1913 7.33649 26.1948 4.52555 22.9529 7.27589C24.8233 6.87835 26.9058 6.35945 28.9764 7.35601C28.9718 7.3131 29.0294 7.29955 29.0634 7.33584L29.0311 7.37653C29.957 7.64377 30.4157 8.08599 31.2574 8.49657C31.2561 8.49625 31.1376 8.48634 31.1347 8.487C32.6097 9.53761 33.9609 11.4009 33.7329 13.3456C23.8555 11.906 31.2476 22.4629 34.3047 14.0205C38.0512 14.4906 42.361 17.5748 43.8766 20.5742L43.8772 20.5715ZM33.6306 13.8923C33.2751 15.0288 32.4926 16.4241 31.2275 16.6503L31.2426 16.6568L31.2367 16.6637C31.7759 16.7084 32.3603 16.3022 32.6901 15.9303C32.1517 17.0702 30.5351 17.1451 29.7303 16.2608L29.7816 16.222C30.0214 16.4708 30.3665 16.6315 30.6989 16.6472C27.2874 14.8546 31.3217 13.5311 33.2589 13.8836C33.215 13.8254 32.661 13.9023 32.525 13.7447C32.9058 13.7501 33.2869 13.7653 33.6527 13.8031L33.6306 13.8923Z"
                fill="#101214"
            />
        </svg>
    );
};

const MarketingHoverIcon = () => {
    return (
        <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M35.275 13.0898L24.365 17.4548V29.0948L35.275 33.4598V13.0898Z"
                fill="#1868DA"
            />
            <path
                d="M27.16 18.1797V28.3497H21.455V34.9097H17.09V28.3497H12.725V18.1797H27.16Z"
                fill="#FCA700"
            />
            <path
                d="M27.16 18.1797H24.365V28.3447H27.16V18.1797Z"
                fill="#AE2A19"
            />
            <path
                d="M45.1753 32.9691C48.8387 27.5787 48.1016 23.1083 47.5146 20.1523C48.032 18.5841 43.8985 11.5261 44.9972 15.9212C46.6134 17.8095 47.4089 24.2138 45.8623 27.7428C46.7052 28.7757 42.3102 35.4007 45.1753 32.9664L45.1753 32.9691Z"
                fill="#101214"
            />
            <path
                d="M42.5772 30.0452C44.5494 27.9498 44.3407 24.87 44.1849 23.6005C44.8247 24.6226 44.5299 21.9032 44.2044 21.462C44.2323 21.8924 43.9457 21.6665 43.9151 21.3088C44.6829 21.1958 42.8832 16.4725 41.9541 17.7582C42.2629 18.3312 41.9875 18.6163 42.5994 18.6674C41.8734 18.9552 42.911 19.5442 42.9916 19.7406C42.8498 20.1602 42.6745 20.1494 43.0946 20.6955C43.0946 21.4271 42.8859 22.4734 43.1224 23.7026C43.1808 25.416 42.6328 26.3467 42.4909 27.7643C42.2628 28.3614 40.8053 31.3444 42.5716 30.0506L42.5772 30.0452Z"
                fill="#101214"
            />
            <path
                d="M39.7622 27.4687C40.4938 26.6295 40.9249 25.5697 40.8275 24.0849C40.2907 24.709 40.3046 24.1253 40.5633 23.7272C41.153 24.518 41.0083 22.03 40.5021 22.6702C40.3797 22.5034 40.4548 22.3178 40.5939 22.4442C41.0306 21.5162 38.8943 18.6785 39.356 20.9864C39.7343 20.8788 38.8108 21.2876 39.7149 21.5916C39.4645 21.9466 39.3282 21.8336 39.6759 22.1456C39.6537 22.4738 39.2364 23.1193 39.5035 23.7783C39.7316 24.7171 38.8804 25.0587 39.3783 25.7634C39.0056 26.183 38.6134 28.1842 39.765 27.4633L39.7622 27.4687Z"
                fill="#101214"
            />
        </svg>
    );
};

const DesignHoverIcon = () => {
    return (
        <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M30.5149 25.3153C30.5949 24.8703 30.6349 24.3903 30.6349 23.8903C30.6349 23.3903 30.5949 22.9003 30.5099 22.4453C30.0249 19.7503 28.1549 18.0903 25.5249 17.6603C25.0749 17.5853 24.6049 17.5503 24.1199 17.5503C23.6349 17.5503 23.1199 17.5903 22.6549 17.6703C20.0299 18.1103 18.1599 19.7653 17.6749 22.4453C17.5949 22.9003 17.5499 23.3803 17.5499 23.8903C17.5499 24.4003 17.5899 24.8703 17.6699 25.3153C18.1499 27.9903 20.0199 29.6503 22.6549 30.0903C23.1199 30.1703 23.6099 30.2103 24.1199 30.2103C24.6299 30.2103 25.0799 30.1753 25.5249 30.1003C28.1549 29.6703 30.0299 28.0103 30.5099 25.3153H30.5149Z"
                fill="#FCA700"
            />
            <path
                d="M8.84497 23.1801L16.645 9.81006L24.445 23.1801H8.84497Z"
                fill="#BF63F3"
            />
            <path
                d="M21.415 17.9902C19.445 18.6802 18.0749 20.2052 17.6749 22.4452C17.6299 22.6852 17.6 22.9252 17.575 23.1802H24.44L21.415 17.9902Z"
                fill="#AE2A19"
            />
            <path
                d="M35.8999 25.0601H24.9399V36.0201H35.8999V25.0601Z"
                fill="#82B536"
            />
            <path
                d="M24.9399 25.0649V30.1749C25.1399 30.1549 25.3349 30.1299 25.5299 30.0999C28.1599 29.6749 30.0299 28.0099 30.5149 25.3149C30.5299 25.2349 30.5449 25.1499 30.5549 25.0649H24.9399Z"
                fill="#AE2A19"
            />
            <path
                d="M39.1887 28.9301C39.2072 28.7551 38.3134 26.6288 38.0682 27.3342C38.1994 27.6824 38.2807 28.1427 38.3915 28.4922C38.4672 28.7755 38.5422 29.1161 38.6053 29.413C38.5001 29.3294 38.3859 29.2435 38.2567 29.1544C38.2591 29.227 38.2608 29.3011 38.2984 29.358C37.1415 28.0774 35.216 27.3418 33.6103 26.5693C33.6122 26.6208 33.5482 26.6341 33.5103 26.5927L33.5482 26.5451C32.5066 26.2686 31.9057 25.8661 30.8386 25.7841C30.8386 25.7841 30.9702 25.7417 30.9736 25.7414C28.0471 25.0023 24.52 24.454 21.5768 25.9355C19.2132 21.1728 12.3985 21.5929 10.1171 26.4704C10.1056 26.563 10.2524 26.4383 10.6155 26.063L10.5965 26.1836C11.2414 25.2622 12.1663 24.6788 12.9528 24.0231C13.038 23.7802 13.0333 23.7537 13.3646 23.5979C14.781 23.5287 16.1215 22.3002 18.1474 23.4679C18.1268 23.4657 17.9956 23.4664 17.9779 23.469C18.6481 23.7978 20.0654 24.6101 20.5309 25.275C20.6697 25.4504 20.6721 25.4934 20.4369 25.3186C20.6386 25.6093 20.8382 25.9269 21.0213 26.2704C20.4153 26.6629 19.8717 27.2124 19.4844 27.809L19.5033 27.7965L19.5042 27.8092C19.6742 27.5361 20.743 26.5858 21.1051 26.4317C18.836 28.2891 18.8209 29.7411 18.8226 29.6371C18.53 33.0067 24.4332 31.7959 22.0886 26.9387C22.0977 27.0631 22.3957 27.6393 22.2573 27.8044C22.1534 27.3986 21.9921 27.0212 21.8169 26.6674C21.8461 26.6577 21.8821 26.6179 21.9069 26.6255C21.9069 26.6255 21.9054 26.6214 21.9047 26.6193C23.5031 25.8042 25.4268 25.7381 27.0668 25.9955L27.032 25.966C31.2015 25.981 35.1655 27.7223 38.5886 30.0668C38.5763 30.1538 38.5703 30.2191 38.5765 30.2202C38.2421 30.4234 37.1892 30.3914 36.9711 30.7093L36.4233 30.7335C36.2376 30.8897 35.9893 31.0004 36.106 31.1066C36.2227 31.2128 38.7304 31.0077 39.3386 30.5882L39.3303 30.5845C39.3303 30.5845 39.528 30.4756 39.528 30.5053C39.517 30.0066 39.5284 29.3015 39.1886 28.9265L39.1887 28.9301ZM21.695 28.0507C22.2985 33.237 16.8435 29.935 21.3472 26.955C21.4955 27.3021 21.6142 27.6674 21.695 28.0507ZM38.2984 29.358C38.3233 29.3429 38.3437 29.3346 38.3525 29.3333C38.3471 29.3343 38.3315 29.3396 38.2984 29.358ZM38.3533 29.3389L38.3385 29.3462C38.3499 29.3391 38.3573 29.3337 38.3525 29.3333C38.358 29.3322 38.3593 29.3328 38.3519 29.3383L38.3533 29.3389Z"
                fill="#101214"
            />
        </svg>
    );
};

const ProjectManagementHoverIcon = () => {
    return (
        <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M39.9298 21.7451H15.5748V33.6451H39.9298V21.7451Z"
                fill="#BF63F3"
            />
            <path
                d="M32.3599 14.355H8.00488V26.255H32.3599V14.355Z"
                fill="#FCA700"
            />
            <path
                d="M32.3598 21.7451H15.5748V26.2551H32.3598V21.7451Z"
                fill="#AE2A19"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M38.4148 26.045L33.1698 31.29L30.4598 28.505L31.6298 27.37L33.1898 28.97L37.2648 24.895L38.4198 26.05L38.4148 26.045Z"
                fill="white"
            />
            <path
                d="M21.8364 10.764C21.5613 10.7076 21.4097 10.4362 21.0784 10.5974C20.0846 11.0837 18.8128 12.6744 18.4113 13.3353C17.4484 13.827 15.7302 16.0249 15.0424 17.1615C14.6297 17.895 13.4702 19.7972 13.4702 19.7972L13.327 19.7866C13.327 19.7866 11.0979 16.9223 9.96369 16.3366C9.76156 16.2721 9.53977 16.3339 9.34606 16.6214C9.24218 16.7235 10.0086 17.6316 10.1883 17.6424C10.3624 17.551 11.1625 18.7225 11.651 19.3137C13.181 21.173 13.4786 21.3341 14.2001 20.7377C15.4017 19.7435 20.5927 12.6475 21.8364 10.7613V10.764Z"
                fill="#101214"
            />
        </svg>
    );
};

const ITHoverIcon = () => {
    return (
        <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.025 17V37.7L18.02 32.56H36.015V17H12.025Z"
                fill="#FCA700"
            />
            <path
                d="M22.68 17.615C22.68 16.13 23.865 14.94 25.33 14.94C26.795 14.94 27.98 16.125 27.98 17.615C27.98 18.37 27.8 18.715 27.635 18.935C27.44 19.19 27.195 19.37 26.765 19.685C26.695 19.74 26.615 19.795 26.53 19.855C25.995 20.25 25.315 20.79 24.805 21.685C24.29 22.585 24.005 23.715 24.005 25.21H26.655C26.655 24.05 26.87 23.405 27.1 23.01C27.33 22.605 27.645 22.33 28.105 21.99C28.16 21.95 28.22 21.905 28.285 21.86C28.7 21.56 29.29 21.135 29.735 20.55C30.31 19.8 30.63 18.86 30.63 17.615C30.63 14.685 28.28 12.29 25.33 12.29C22.38 12.29 20.03 14.685 20.03 17.615V18.94H22.68V17.615Z"
                fill="#1868DB"
            />
            <path
                d="M25.33 27.2051C24.415 27.2051 23.675 27.9451 23.675 28.8601C23.675 29.7751 24.415 30.5151 25.33 30.5151C26.245 30.5151 26.9851 29.7751 26.9851 28.8601C26.9851 27.9451 26.245 27.2051 25.33 27.2051Z"
                fill="#1868DB"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20.06 17H22.75C22.7 17.195 22.675 17.405 22.675 17.615V18.94H20.025V17.615C20.025 17.41 20.035 17.205 20.06 17Z"
                fill="#AE2A19"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M26.985 28.8601C26.985 29.7751 26.245 30.5201 25.33 30.5201C24.415 30.5201 23.67 29.7751 23.67 28.8601C23.67 27.9451 24.415 27.2051 25.33 27.2051C26.245 27.2051 26.985 27.9451 26.985 28.8601Z"
                fill="#AE2A19"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M30.63 17.615C30.63 18.865 30.31 19.8 29.735 20.55C29.29 21.135 28.7 21.56 28.285 21.86C28.22 21.91 28.16 21.95 28.105 21.99C27.645 22.335 27.33 22.61 27.1 23.015C26.87 23.415 26.655 24.06 26.655 25.215H24.005C24.005 23.72 24.285 22.59 24.8 21.695C25.315 20.8 25.995 20.26 26.525 19.86C26.61 19.8 26.69 19.745 26.76 19.69C27.195 19.37 27.435 19.195 27.63 18.94C27.8 18.715 27.98 18.37 27.98 17.615C27.98 17.405 27.955 17.195 27.905 17H30.595C30.62 17.205 30.63 17.41 30.63 17.615Z"
                fill="#AE2A19"
            />
            <path
                d="M34.5697 15.1083C34.4693 15.1182 33.9074 12.5161 34.0093 11.4227C33.6444 11.2413 33.2977 10.7244 33.2604 10.1521C31.7036 9.1806 32.8696 11.9428 33.2518 12.799C33.4405 12.1784 33.3856 13.7041 33.5953 13.9163C33.5716 13.7897 34.0709 16.3169 34.4795 15.562C34.5476 15.6349 34.6852 15.0233 34.5714 15.1092L34.5697 15.1083Z"
                fill="#101214"
            />
            <path
                d="M39.9913 11.837C39.8428 11.283 40.0409 10.7819 40.3901 10.2892C39.9708 8.36464 38.7519 11.6022 38.4664 12.3908C38.4664 12.3908 37.9363 13.7652 37.9139 13.6048C37.6718 13.7302 36.4316 15.6891 37.3611 15.5516C37.6018 15.2416 37.7836 14.5187 38.0738 14.4976C38.707 14.2909 39.2768 12.426 39.9904 11.8343L39.9913 11.837Z"
                fill="#101214"
            />
            <path
                d="M45.0564 16.4758C45.2541 15.2209 43.0228 16.3653 42.1192 16.4997C42.1192 16.4997 41.1768 16.5872 40.933 16.7784C41.0701 16.7661 37.7434 17.2742 39.5564 17.7527C39.6022 17.7319 40.7541 17.5579 40.7541 17.5579C42.3015 17.7804 43.855 17.1558 45.0574 16.4739L45.0564 16.4758Z"
                fill="#101214"
            />
        </svg>
    );
};

const teamsData: { [key in TeamType]: Team } = {
    "default": {
        videoLink:
            "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_FULL_LowBR.mp4",
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_A_HD (0-00-00-00)-4060786602.webp"
    },
    "software": {
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/Jira-Board-Software-3233077719.webp"
    },
    "product-management": {
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/Jira-Timeline-Product Management-3301759489.webp"
    },
    "marketing": {
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/Jira-Calendar-Marketing-4146978900.webp"
    },
    "project-management": {
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/Jira-Timeline-Project Management-2679766879.webp"
    },
    "design": {
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/Jira-List-Design-1069502226.webp"
    },
    "it": {
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/Jira-On-call-IT-3030871761.webp"
    }
};

type cardInfo = {
    teamType: TeamType;
    name: string;
    icon?: ReactElement;
    iconHover?: ReactElement;
};

const cardData: cardInfo[] = [
    {
        teamType: "software",
        name: "Software",
        icon: <SoftwareIcon />,
        iconHover: <SoftwareHoverIcon />
    },
    {
        teamType: "product-management",
        name: "Product Management",
        icon: <ProductManagementIcon />,
        iconHover: <ProductManagementHoverIcon />
    },
    {
        teamType: "marketing",
        name: "Marketing",
        icon: <MarketingIcon />,
        iconHover: <MarketingHoverIcon />
    },
    {
        teamType: "project-management",
        name: "Project Management",
        icon: <ProjectManagementIcon />,
        iconHover: <ProjectManagementHoverIcon />
    },
    {
        teamType: "design",
        name: "Design",
        icon: <DesignIcon />,
        iconHover: <DesignHoverIcon />
    },
    {
        teamType: "it",
        name: "IT",
        icon: <ITIcon />,
        iconHover: <ITHoverIcon />
    }
];

/* circle chevron icon */

type CircleChevronProps = {
    type: "left" | "right";
};

const CircleChevron = ({ type }: CircleChevronProps) => {
    return (
        <svg
            width="40"
            height="41"
            viewBox="0 0 40 41"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={
                type === "left"
                    ? {
                          transform: "scale(-1, 1)"
                      }
                    : undefined
            }
        >
            <circle cx="20" cy="20.5" r="19" stroke="#101214" strokeWidth="2" />
            <path
                d="M18.0488 28.2852L24.6785 21.6554C25.1072 21.2268 25.1072 20.5318 24.6785 20.1032L18.0488 13.4735L16.4966 15.0257L22.3503 20.8793L16.4966 26.733L18.0488 28.2852Z"
                fill="#101214"
            />
        </svg>
    );
};

export const HeroJiraCTA = () => {
    const showTreatmentHero = FeatureGates.getExperimentValue(
        "gwp-1705-homepage-hero",
        "showTreatment",
        false
    );

    const intl = useIntl();

    const leftArrowTranslatedLabel = intl.formatMessage({
        id: "HeroJiraCTA.leftArrow.aria-label",
        defaultMessage: "Left arrow"
    });

    const leftArrowDataEventProps: DataEventProps = {
        "data-product-key": "home",
        "data-event-component": "button",
        "data-event-container": "heroSection",
        "data-event": "clicked",
        "data-label": leftArrowTranslatedLabel,
        "data-label-english": "Left arrow"
    };

    const rightArrowTranslatedLabel = intl.formatMessage({
        id: "HeroJiraCTA.rightArrow.aria-label",
        defaultMessage: "Right arrow"
    });

    const rightArrowDataEventProps: DataEventProps = {
        "data-product-key": "home",
        "data-event-component": "button",
        "data-event-container": "heroSection",
        "data-event": "clicked",
        "data-label": rightArrowTranslatedLabel,
        "data-label-english": "Right arrow"
    };

    /* variant 1 */

    const LeftArrow = ({ onClick }: { onClick?: () => void }) => (
        <button
            aria-label={leftArrowTranslatedLabel}
            className="button-left"
            onClick={onClick}
            {...leftArrowDataEventProps}
        >
            <CircleChevron type="left" />
        </button>
    );
    const RightArrow = ({ onClick }: { onClick?: () => void }) => (
        <button
            aria-label={rightArrowTranslatedLabel}
            className="button-right"
            onClick={onClick}
            {...rightArrowDataEventProps}
        >
            <CircleChevron type="right" />
        </button>
    );

    const getSlideSpeed = (currSlide: number) => {
        return jiraViews[currSlide]?.videoLength;
    };

    const SLIDER_TRANSITION_INTERVAL = 600;
    const [slideSpeed, setSlideSpeed] = useState(getSlideSpeed(0));
    const handleAfterChange = (slide: number) => {
        const slideSpeed = getSlideSpeed(slide);
        if (slideSpeed) {
            setSlideSpeed(slideSpeed - SLIDER_TRANSITION_INTERVAL);
        }
    };

    /* variant 2 */

    const [team, setTeam] = useState<TeamType>("default");
    const cardRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    const { videoLink, image } = teamsData[team];

    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (
            cardRef.current &&
            !cardRef.current.contains(event.target as Node)
        ) {
            setTeam("default");
        }
    }, []);

    useEffect(() => {
        // Attach the listener on component mount
        document.addEventListener("mousedown", handleClickOutside);

        // Detach the listener on component unmount
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    return (
        <UFOSegment name="homepage-v2-hero">
            <TypographyStyles>
                <section id="hero-jira-cta" css={jiraHeroStyles}>
                    <Row>
                        <div>
                            <h2 className="superhero-contrast">
                                <FormattedMessage
                                    id="HomePageTemplateV2.jiraHero"
                                    defaultMessage="The new Jira:{br}from <teams>teams</teams> to dreams"
                                    values={{
                                        teams: (str: ReactNode) => (
                                            <Adg4TextUnderline
                                                underline={
                                                    <svg
                                                        width="100%"
                                                        height="30"
                                                        viewBox="0 0 306 42"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M0.314542 22.3672C52.9464 14.9125 91.8245 33.6666 144.966 18.1254C147.46 24.7349 153.892 33.0058 163.497 33.1094C155.486 32.9725 142.641 15.7178 155.536 27.2686C163.119 36.0683 196.172 25.2364 170.842 32.944C188.334 31.2885 204.356 20.5339 216.163 10.9702C235.811 -2.65832 220.928 23.6737 235.178 36.8756C255.37 55.2242 316.463 7.29572 304.134 7.28816C296.296 13.2947 285.769 17.046 281.423 23.6985C204.757 63.1757 259.992 -9.72674 214.311 2.55426C139.744 53.2533 177.393 7.4163 133.572 10.6223C108.06 28.9946 -0.613806 0.46356 0.29434 22.3672L0.314542 22.3672Z"
                                                            fill="#FFA900"
                                                        />
                                                    </svg>
                                                }
                                            >
                                                {str}
                                            </Adg4TextUnderline>
                                        ),
                                        br: <br />
                                    }}
                                />
                            </h2>

                            <Adg4Button
                                variant="primary"
                                color="blue"
                                href={useLocalizedHref("/signup")}
                                dataEventProps={{
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container": "heroSection",
                                    "data-product-key": "home",
                                    "data-label-english": "Get started"
                                }}
                            >
                                {
                                    <FormattedMessage
                                        id="HomePageTemplateV2.jiraHero.getStartedCtaButton"
                                        defaultMessage="Get started"
                                    />
                                }
                            </Adg4Button>

                            {showTreatmentHero && (
                                <div ref={cardRef}>
                                    <div ref={scrollRef}>
                                        <Adg4Tabs>
                                            <Adg4DraggableTabList
                                                css={tabsStyle}
                                                className={
                                                    sectionIsVisible
                                                        ? "is-visible"
                                                        : ""
                                                }
                                            >
                                                {cardData.map(
                                                    ({
                                                        name,
                                                        teamType,
                                                        icon,
                                                        iconHover
                                                    }) => {
                                                        return (
                                                            <Adg4DraggableTab
                                                                key={teamType}
                                                            >
                                                                <button
                                                                    onClick={() => {
                                                                        setTeam(
                                                                            teamType
                                                                        );
                                                                    }}
                                                                    data-product-key="home"
                                                                    data-event-component="button"
                                                                    data-event-container="heroSection"
                                                                    data-event="clicked"
                                                                    data-label-english={
                                                                        name
                                                                    }
                                                                >
                                                                    <div
                                                                        className={`team-card ${
                                                                            team ==
                                                                            teamType
                                                                                ? "active"
                                                                                : "inactive"
                                                                        }`}
                                                                    >
                                                                        <div className="default-icon">
                                                                            {
                                                                                icon
                                                                            }
                                                                        </div>
                                                                        <div className="hover-icon">
                                                                            {iconHover ??
                                                                                iconHover}
                                                                        </div>
                                                                        <span>
                                                                            {
                                                                                name
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                </button>
                                                            </Adg4DraggableTab>
                                                        );
                                                    }
                                                )}
                                            </Adg4DraggableTabList>
                                        </Adg4Tabs>
                                    </div>
                                </div>
                            )}

                            <Row layout="10">
                                <div>
                                    <div css={videoContainerStyles}>
                                        {showTreatmentHero ? (
                                            <div>
                                                {videoLink ? (
                                                    <Video
                                                        videoLink={videoLink}
                                                        // !!!TODO: ADJUST DEPENDING ON FORMAT OF FINAL ASSETS
                                                        videoType="mp4"
                                                        className="video"
                                                        videoPreviewImage={
                                                            image
                                                        }
                                                    >
                                                        <Picture
                                                            className="fallback-img--desktop"
                                                            image={{
                                                                src: image,
                                                                alt: "",
                                                                aspectRatio:
                                                                    "none"
                                                            }}
                                                        />
                                                    </Video>
                                                ) : (
                                                    <Picture
                                                        className="fallback-img--desktop"
                                                        image={{
                                                            src: image,
                                                            alt: "",
                                                            aspectRatio: "none"
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        ) : (
                                            <div className="carousel-container">
                                                <div css={slickStyle}>
                                                    <Slider
                                                        prevArrow={
                                                            <LeftArrow />
                                                        }
                                                        nextArrow={
                                                            <RightArrow />
                                                        }
                                                        slidesToScroll={1}
                                                        autoplay={true}
                                                        autoplaySpeed={
                                                            slideSpeed
                                                        }
                                                        speed={
                                                            SLIDER_TRANSITION_INTERVAL
                                                        }
                                                        afterChange={(
                                                            currentSlide
                                                        ) =>
                                                            handleAfterChange(
                                                                currentSlide
                                                            )
                                                        }
                                                    >
                                                        {jiraViews.map(
                                                            (
                                                                {
                                                                    videoLink,
                                                                    image
                                                                },
                                                                index
                                                            ) => (
                                                                <Video
                                                                    videoLink={
                                                                        videoLink
                                                                    }
                                                                    // !!!TODO: ADJUST DEPENDING ON FORMAT OF FINAL ASSETS
                                                                    videoType="mp4"
                                                                    className="video slick-item"
                                                                    // !!!TODO: REPLACE PLACEHOLDER IMAGE
                                                                    videoPreviewImage={
                                                                        image
                                                                    }
                                                                    key={index}
                                                                >
                                                                    <Picture
                                                                        className="fallback-img--desktop"
                                                                        image={{
                                                                            // !!!TODO: REPLACE PLACEHOLDER IMAGE
                                                                            src: image,
                                                                            alt: "",
                                                                            aspectRatio:
                                                                                "none"
                                                                        }}
                                                                    />
                                                                </Video>
                                                            )
                                                        )}
                                                    </Slider>
                                                </div>
                                            </div>
                                        )}
                                        <div css={backgroundStyles} />
                                    </div>
                                </div>
                            </Row>
                        </div>
                    </Row>
                </section>
            </TypographyStyles>
        </UFOSegment>
    );
};

const jiraHeroStyles = css({
    textAlign: "center",
    overflow: "hidden",

    h2: {
        marginBlock: "19px"
    },

    a: {
        padding: "15px 30px",
        fontSize: "1.25rem",
        fontWeight: "300",
        lineHeight: "15px",
        marginBlock: "30px 40px",

        [media.below.sm]: {
            marginBlock: "20px 45px"
        }
    },

    button: {
        "all": "unset",
        "width": "100%",

        ":hover": {
            cursor: "pointer"
        }
    }
});

const tabsStyle = css({
    "display": "flex",
    "justifyContent": "space-between",
    "margin": "0 auto",
    "marginBlockEnd": "50px",

    ".team-card": {
        "boxSizing": "border-box",
        "display": "flex",
        "flexDirection": "column",
        "width": "173px",
        "height": "90px",
        "alignItems": "center",
        "padding": "8px",

        "span": {
            fontFamily: "var(--bxp-charlie-display-font-family)",
            fontWeight: 400,
            fontSize: "1rem",
            lineHeight: "1.5rem",
            color: "#101214"
        },

        ".hover-icon": {
            display: "none"
        },

        ":hover": {
            ".default-icon": {
                display: "none"
            },

            ".hover-icon": {
                display: "block"
            }
        }
    },

    ".active": {
        border: "1px solid #101214",
        borderRadius: "6px",

        span: {
            fontWeight: "700"
        }
    },

    "&.is-visible": {
        [media.below.md]: {
            "@keyframes pull": {
                "0%": {
                    marginInlineStart: "0",
                    transform: "margin"
                },
                "50%": {
                    marginInlineStart: "-120px",
                    transform: "margin"
                },
                "100%": {
                    marginInlineStart: "0",
                    transform: "margin"
                }
            },
            "animation": "2s pull forwards"
        }
    }
});

const videoContainerStyles = css({
    "width": "100%",
    "aspectRatio": "16/9",
    "position": "relative",
    "marginBlockEnd": "130px",

    ".video": {
        width: "100%",
        borderRadius: "5px",
        boxShadow:
            "0px 0px 1px 0px rgba(9, 30, 66, 0.12), 0px 0px 8px 0px rgba(9, 30, 66, 0.16)"
    },

    [media.below.sm]: {
        marginBlockEnd: "100px"
    },

    [media.below.xs]: {
        marginBlockEnd: "60px"
    },

    ".carousel-container": {
        margin: "0 auto",

        [media.below.md]: {
            width: "90%"
        },

        [media.below.sm]: {
            width: "100%"
        }
    },

    ".button-left": {
        zIndex: "2",
        position: "absolute",
        width: "fit-content",
        insetBlockStart: "40%",
        insetInlineStart: "-100px",

        [media.below.md]: {
            insetInlineStart: "-75px"
        },

        [media.below.sm]: {
            display: "none"
        }
    },

    ".button-right": {
        zIndex: "2",
        position: "absolute",
        width: "fit-content",
        insetBlockStart: "40%",
        insetInlineEnd: "-100px",

        [media.below.md]: {
            insetInlineEnd: "-75px"
        },

        [media.below.sm]: {
            display: "none"
        }
    }
});

const backgroundStyles = css({
    position: "absolute",
    insetInlineStart: "-100px",
    insetBlockStart: "58%",
    width: "calc(100% + 200px)",
    height: "55%",
    backgroundColor: "#1868DB",
    zIndex: "-1",
    clipPath:
        "polygon(0.5% 0, 50% 0, 50% 10%, 98.5% 0%, 100% 85%, 17.75% 100%, 17.25% 85%, 0 85%)",

    [media.below.sm]: {
        insetInlineStart: "-50px"
    }
});

// Source https://github.com/kenwheeler/slick/blob/master/slick/slick.css
const slickStyle = css({
    ".slick-slider": {
        position: "relative",
        display: "block",
        boxSizing: "border-box",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        MsUserSelect: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",
        KhtmlUserSelect: "none",
        MsTouchAction: "pan-y",
        touchAction: "pan-y",
        WebkitTapHighlightColor: "transparent"
    },
    ".slick-list": {
        position: "relative",
        display: "block",
        overflow: "hidden",
        padding: 0,
        marginInline: "-30px"
    },
    ".slick-list:focus": {
        outline: "none"
    },
    ".slick-list.dragging": {
        cursor: "pointer"
    },
    ".slick-slider .slick-track, .slick-slider .slick-list": {
        WebkitTransform: "translate3d(0, 0, 0)",
        MozTransform: "translate3d(0, 0, 0)",
        MsTransform: "translate3d(0, 0, 0)",
        OTransform: "translate3d(0, 0, 0)",
        transform: "translate3d(0, 0, 0)"
    },
    ".slick-track": {
        position: "relative",
        insetBlockStart: 0,
        insetInlineStart: 0,
        display: "block",
        marginInlineStart: "auto",
        marginInlineEnd: "auto"
    },
    ".slick-track:before, .slick-track:after": {
        display: "table",
        content: "''"
    },
    ".slick-track:after": {
        clear: "both"
    },
    ".slick-loading .slick-track": {
        visibility: "hidden"
    },
    ".slick-slide": {
        display: "none",
        float: "left",
        height: "100%",
        minHeight: "1px"
    },
    "[dir='rtl'] .slick-slide": {
        float: "right"
    },
    ".slick-slide img": {
        display: "block"
    },
    ".slick-slide.slick-loading img": {
        display: "none"
    },
    ".slick-slide.dragging img": {
        pointerEvents: "none"
    },
    ".slick-initialized .slick-slide": {
        display: "block"
    },
    ".slick-loading .slick-slide": {
        visibility: "hidden"
    },
    ".slick-vertical .slick-slide": {
        display: "block",
        height: "auto",
        border: "1px solid transparent"
    },
    ".slick-arrow.slick-hidden": {
        display: "none"
    },
    ".slick-slide > div": {
        marginInline: "30px"
    }
});
